import { type AuthenticationResult, PublicClientApplication } from '@azure/msal-browser'

// Microsoft / Entra sign-in for vetpo via MSAL.js. Multi-tenant (`/common`) by
// default so any vet clinic's Microsoft 365 tenant can sign in; the backend
// validates the tenant per-token. The client ID comes from VITE_AZURE_CLIENT_ID
// (the SPA app registration). When unset, Microsoft sign-in is disabled and the
// button is hidden (see OAuthButtons).

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined
const authority =
  (import.meta.env.VITE_AZURE_AUTHORITY as string | undefined) ||
  'https://login.microsoftonline.com/common'

export const microsoftEnabled = Boolean(clientId)

let instance: PublicClientApplication | null = null
let initializing: Promise<void> | null = null

const getInstance = async (): Promise<PublicClientApplication> => {
  if (!clientId) throw new Error('Microsoft sign-in is not configured')
  if (!instance) {
    instance = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        // vetpo is served under /vet-portal/; keep the redirect on that path.
        redirectUri: `${window.location.origin}/vet-portal/`,
      },
      // sessionStorage mirrors the vetpo token lifetime (per-tab, cleared on close).
      cache: { cacheLocation: 'sessionStorage' },
    })
  }
  // MSAL v3+ requires initialize() before any other call.
  if (!initializing) initializing = instance.initialize()
  await initializing
  return instance
}

/**
 * Open the Microsoft sign-in popup and return the ID token to POST to the
 * backend's /api/users/oauth/login endpoint.
 */
export const signInWithMicrosoft = async (): Promise<string> => {
  const msal = await getInstance()
  const result: AuthenticationResult = await msal.loginPopup({
    scopes: ['openid', 'profile', 'email'],
    prompt: 'select_account',
  })
  if (!result.idToken) throw new Error('No ID token returned from Microsoft')
  return result.idToken
}
